import { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Button,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useAuthContext } from '../../auth/useAuthContext';
import axios from '../../utils/Axios';
import Logo from '../logo';
import Iconify from '../iconify';
import { useTheme } from '@mui/material/styles';
import confetti from 'canvas-confetti';

export default function ConnectWebflowStep({ onBack, onComplete }) {
  const { user, setGlobalUser } = useAuthContext();
  const theme = useTheme();
  const [connectedSites, setConnectedSites] = useState([]);
  const [isCheckingBooking, setIsCheckingBooking] = useState(false);
  const [bookingStatus, setBookingStatus] = useState(null);
  const [isRefreshingSites, setIsRefreshingSites] = useState(false);
  const [currentStep, setCurrentStep] = useState('install'); // 'install' or 'verify'
  const [pollCount, setPollCount] = useState(0);

  // Polling configuration: 24 attempts × 10 seconds = 4 minutes max
  const POLLING_INTERVAL = 10000; // 10 seconds
  const MAX_POLL_ATTEMPTS = 24;

  useEffect(() => {
    setConnectedSites(user?.config?.webflowSites || []);
  }, [user]);

  // Trigger confetti when booking is successfully received
  useEffect(() => {
    if (bookingStatus === true && connectedSites.length > 0) {
      // Small delay to let the success message render first
      const timer = setTimeout(() => {
        triggerConfetti();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [bookingStatus, connectedSites.length]);

  // Smart auto-polling: Check for Webflow connection every 10 seconds
  // Stops automatically when sites are detected or max attempts reached
  useEffect(() => {
    // Only poll when on verify step, no sites connected yet, and haven't exceeded max attempts
    if (currentStep === 'verify' && connectedSites.length === 0 && pollCount < MAX_POLL_ATTEMPTS) {
      console.log(`[Webflow Polling] Starting check ${pollCount + 1}/${MAX_POLL_ATTEMPTS}`);

      const intervalId = setInterval(() => {
        handleRefreshConnectedSites();
        setPollCount((prev) => prev + 1);
      }, POLLING_INTERVAL);

      return () => {
        console.log('[Webflow Polling] Cleaning up interval');
        clearInterval(intervalId);
      };
    }

    // Log when polling stops
    if (connectedSites.length > 0) {
      console.log('[Webflow Polling] Sites detected - polling stopped');
    } else if (pollCount >= MAX_POLL_ATTEMPTS) {
      console.log('[Webflow Polling] Max attempts reached - polling stopped');
    }
  }, [currentStep, connectedSites.length, pollCount, MAX_POLL_ATTEMPTS, POLLING_INTERVAL]);

  // Reset poll count when sites are detected or when leaving verify step
  useEffect(() => {
    if (connectedSites.length > 0 || currentStep !== 'verify') {
      setPollCount(0);
    }
  }, [connectedSites.length, currentStep]);

  const handleInstallWebflow = () => {
    window.open('https://app-api.flowbookings.io/authorize', '_blank');
  };

  const handleCheckBooking = async () => {
    setIsCheckingBooking(true);
    try {
      // Actually check for real bookings from the bookings API
      const response = await axios.get('/bookings');
      const bookings = response.data.bookings || [];
      
      // Check if user has at least 1 booking
      const hasTestBooking = bookings.length > 0;
      setBookingStatus(hasTestBooking);
      setIsCheckingBooking(false);
    } catch (error) {
      console.error('Error checking bookings:', error);
      setIsCheckingBooking(false);
      setBookingStatus(false);
    }
  };

  const handleRefreshConnectedSites = async () => {
    setIsRefreshingSites(true);
    try {
      // Fetch latest user data from backend
      const response = await axios.get('/user');
      const { user: updatedUser } = response.data;
      
      if (updatedUser) {
        // Update the global user state with fresh data
        setGlobalUser(updatedUser);
        // The useEffect will automatically update connectedSites when user changes
      }
    } catch (error) {
      console.error('Error refreshing connected sites:', error);
    } finally {
      setIsRefreshingSites(false);
    }
  };

  const handleFinishSetup = () => {
    // Warn user if Webflow sites aren't connected (edge case)
    // This is required for backend to mark onboarding as complete
    if (connectedSites.length === 0) {
      alert('Please ensure you have connected at least one Webflow site to complete onboarding. Your setup may not be marked as complete without this connection.');
    }
    onComplete();
  };

  const handleBack = () => {
    if (currentStep === 'verify') {
      // If on verify step, go back to install step
      setCurrentStep('install');
    } else {
      // If on install step, go back to previous onboarding step
      onBack();
    }
  };

  const triggerConfetti = () => {
    // Multiple confetti bursts for extra celebration
    const count = 200;
    const defaults = {
      origin: { y: 0.7 }
    };

    function fire(particleRatio, opts) {
      confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio),
      });
    }

    fire(0.25, {
      spread: 26,
      startVelocity: 55,
    });
    fire(0.2, {
      spread: 60,
    });
    fire(0.35, {
      spread: 100,
      decay: 0.91,
      scalar: 0.8
    });
    fire(0.1, {
      spread: 120,
      startVelocity: 25,
      decay: 0.92,
      scalar: 1.2
    });
    fire(0.1, {
      spread: 120,
      startVelocity: 45,
    });
  };

  return (
    <Box sx={{ py: 2 }}>
      
      <Typography variant="h4" sx={{ textAlign: 'center', mb: 2, fontWeight: 700 }}>
        {currentStep === 'verify' ? 'Verify Webflow Site & Check Booking' : 'Connect Webflow'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 4, textWrap: 'balance' }}>
        {currentStep === 'verify'
          ? 'Verify if the webflow site is connected to Flowbookings, and create a test booking.'
          : 'Install the FlowBookings Webflow app and add a booking form to your website! If you already have the app installed click next and proceed to the next step.'
        }
      </Typography>

      {currentStep === 'install' ? (
        <>
          <Box sx={{ 
            mb: 4, 
            py: 3, // 34px (34/8 = 4.25)
            px: 3, // 24px (24/8 = 3)
            backgroundColor: theme.palette.grey[100] 
          }}>
            <Typography variant="overline" sx={{ mb: 2 }}>
              INSTALLATION INSTRUCTIONS
            </Typography>
            <div style={{marginBottom: '8px'}}></div>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Follow these instructions to install the FlowBookings app on your Webflow website.
            </Typography>
            
            <Stack spacing={1} sx={{ mb: 3 }}>
              <Typography variant="body2">
                1. Install the <a href='https://webflow.com/apps/detail/flowbookings' target='_blank' style={{color: '#36B37E'}}>Webflow App</a>
              </Typography>
              <Typography variant="body2">
                2. Login to your FlowBookings account in the Webflow App.
              </Typography>
              <Typography variant="body2">
                3. Copy the booking form (with or without customisation).
              </Typography>
              <Typography variant="body2">
                4. Paste the booking form on the page.
              </Typography>
              <Typography variant="body2">
                5. Publish your Webflow project.
              </Typography>
            </Stack>

            <Box sx={{ textAlign: 'center', mb: 8, position: 'relative', display: 'inline-block', width: '100%', marginBottom: 0 }}>
              <img 
                src="https://flowbookings-website.s3.us-east-2.amazonaws.com/Testing+onboarding.png" 
                alt="Webflow Integration Demo"
                style={{ 
                  width: '100%',  
                  borderRadius: 4,
                  
                }}
                onError={(e) => {
                  //e.target.style.display = 'none';
                }}
              />
              {/* YouTube video link overlay in bottom right */}
              <Box
                component="a"
                href="https://www.youtube.com/watch?v=Smk55uTrGfI"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  position: 'absolute',
                  bottom: 4,
                  right: 4,
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: 0.5,
                  fontSize: '12px',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  }
                }}
              >
                <Iconify icon="mdi:play" width={16} height={16} />
                Watch On YouTube
              </Box>
            </Box>
            </Box>
            <Stack direction="row" spacing={2} justifyContent="end" sx={{ mb: 4 }}>
              <Button variant="outlined" onClick={handleBack}>
                Back
              </Button>
              <Button 
                variant="contained" 
                onClick={handleInstallWebflow}
                endIcon={<Iconify icon="tabler:external-link" />}
                sx={{ bgcolor: 'primary.green900' }}
              >
                Install Webflow App
              </Button>
              <Button variant="contained" onClick={() => {
                setCurrentStep('verify');
                handleRefreshConnectedSites();
              }}>
                Next
              </Button>
            </Stack>
          
        </>
      ) : (
          <>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Connected Sites
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                The following Webflow site(s) are connected to your Flowbookings account
              </Typography>

              {/* Polling Status Indicator */}
              {connectedSites.length === 0 && pollCount < MAX_POLL_ATTEMPTS && (
                <Box sx={{
                  bgcolor: 'info.lighter',
                  p: 2,
                  borderRadius: 1,
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}>
                  <Iconify icon="mdi:information-outline" width={20} />
                  <Typography variant="body2" color="info.dark">
                    Auto-checking for connection every 10 seconds... (Attempt {pollCount}/{MAX_POLL_ATTEMPTS})
                  </Typography>
                </Box>
              )}

              {connectedSites.length === 0 && pollCount >= MAX_POLL_ATTEMPTS && (
                <Box sx={{
                  bgcolor: 'warning.lighter',
                  p: 2,
                  borderRadius: 1,
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}>
                  <Iconify icon="mdi:alert-circle-outline" color="warning.dark" width={20} />
                  <Typography variant="body2" color="warning.dark">
                    Max auto-check attempts reached. Click "Check Connected Sites" to manually refresh.
                  </Typography>
                </Box>
              )}

              {connectedSites.length > 0 && (
                <Box sx={{
                  bgcolor: 'success.lighter',
                  p: 2,
                  borderRadius: 1,
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}>
                  <Iconify icon="mdi:check-circle-outline" width={20} />
                  <Typography variant="body2" color="success.dark">
                    Webflow site connected successfully!
                  </Typography>
                </Box>
              )}

              {/* Check Connected Sites Button */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <LoadingButton
                  variant="outlined"
                  loading={isRefreshingSites}
                  onClick={handleRefreshConnectedSites}
                  startIcon={<Iconify icon="mdi:refresh" />}
                  sx={{ 
                    color: 'primary.main',
                    borderColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.lighter'
                    }
                  }}
                >
                  {isRefreshingSites ? 'Checking...' : 'Check Connected Sites'}
                </LoadingButton>
              </Box>
              
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 3 }}>
                <Box sx={{ 
                  bgcolor: 'grey.50', 
                  p: 2, 
                  borderTopLeftRadius: 4, 
                  borderTopRightRadius: 4,
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: 2
                }}>
                  <Typography variant="subtitle2">Name</Typography>
                  <Typography variant="subtitle2">Webflow Site</Typography>
                  <Typography variant="subtitle2">Test Booking Received?</Typography>
                </Box>
                
                {connectedSites.length === 0 ? (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="error.main">
                      No webflow site connected. Please go back to the last step and install the Flowbookings Webflow app on your site.
                    </Typography>
                  </Box>
                ) : (
                  connectedSites.map((site, index) => (
                    <Box key={index} sx={{ 
                      p: 2, 
                      borderTop: 1, 
                      borderColor: 'divider',
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: 2,
                      alignItems: 'center'
                    }}>
                      <Typography variant="body2" color="primary.main">
                        {site.siteName}
                      </Typography>
                      <Typography variant="body2">
                        {`${site.siteId}.webflow.io` || '-'}
                      </Typography>
                      <Typography variant="body2" color={bookingStatus ? 'success.main' : 'error.main'}>
                        {bookingStatus ? 'Booking Received' : 'Booking Not Received'}
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>

              {bookingStatus != true && connectedSites.length > 0 && (
                <Box sx={{ bgcolor: 'warning.lighter', p: 2, borderRadius: 1, mb: 3 }}>
                  <Typography variant="body2" color="warning.dark">
                    <strong>BOOKING NOT RECEIVED:</strong><br />
                    We have not received any bookings from the connected site. Please go back to the previous step and 
                    make sure you add the booking form and publish your project before submitting a test booking on the form.
                  </Typography>
                </Box>
              )}

              {bookingStatus === true && connectedSites.length > 0 && (
                <Box sx={{ bgcolor: 'success.lighter', p: 2, borderRadius: 1, mb: 3 }}>
                  <Typography variant="body2" color="success.dark">
                    <strong>CONGRATULATIONS, YOU GOT YOUR FIRST BOOKING! 🎉</strong><br />
                    You've successfully set up FlowBookings on your Webflow website. You can now go ahead and create 
                    more services, setup payments, and configure more settings in our platform.
                  </Typography>
                </Box>
              )}

              <Stack direction="row" spacing={2} justifyContent="center">
                <Button variant="outlined" onClick={handleBack}>
                  Back
                </Button>
                {bookingStatus === true ? (
                  <Button 
                    variant="contained" 
                    onClick={handleFinishSetup}
                    sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}
                  >
                    Finish Setup & View Booking
                  </Button>
                ) : (
                  <LoadingButton 
                    variant="contained" 
                    loading={isCheckingBooking}
                    onClick={handleCheckBooking}
                    sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}
                  >
                    Check Booking
                  </LoadingButton>
                )}
              </Stack>
            </Box>
          </>
        )}
    </Box>
  );
}